/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { MdOutlineSensorDoor, MdSignalWifiConnectedNoInternet0 } from 'react-icons/md'

import { type SensorStatus, type Units, doorLabel, formatTemp, isOfflineTemperature, offlineTooltip } from '../format'
import type { InStoreSensor, Position } from '../types'

export interface SensorMarkerProps {
  sensor: InStoreSensor
  /** The location's name, shown with the sensor's when surfaces are shared. */
  locationName: string
  status: SensorStatus
  position: Position
  /** Diameter in px; the reading scales with it. */
  size: number
  units: Units
  selected?: boolean
  /** Shows the label without a hover: the selected marker, or the sensor hovered in the list. */
  showLabel?: boolean
  editMode?: boolean
  onClick?: () => void
  onHoverChange?: (hovering: boolean) => void
  handlers?: React.DOMAttributes<HTMLButtonElement>
}

const STATUS_CLASS: Record<SensorStatus, string> = {
  alarm: 'bg-error text-error-content ring-error/40',
  online: 'bg-success text-success-content ring-success/40',
  offline: 'bg-neutral text-neutral-content ring-neutral/40',
}

/**
 * A sensor on the floor plan: a disc with the current reading, green when online, gray
 * when not, red when the reading is outside the location's range. An offline sensor
 * shows no reading; its last known one is in the label. The name appears beside it on
 * hover (of the marker or its row in the list), and stays beside the selected marker
 * unless another is hovered — a label inside the surface rather than a CSS tooltip, which the
 * surface's overflow would clip at the edges.
 */
export const SensorMarker = ({
  sensor,
  locationName,
  status,
  position,
  size,
  units,
  selected,
  showLabel = false,
  editMode,
  onClick,
  onHoverChange,
  handlers,
}: SensorMarkerProps) => {
  const offline = isOfflineTemperature(sensor)
  const reading =
    sensor.type === 'door' ? (
      <MdOutlineSensorDoor style={{ width: size * 0.45, height: size * 0.45 }} aria-label={doorLabel(sensor)} />
    ) : offline ? (
      <MdSignalWifiConnectedNoInternet0
        style={{ width: size * 0.45, height: size * 0.45 }}
        aria-label="Offline"
        data-testid={`marker-offline-${sensor.id}`}
      />
    ) : (
      formatTemp(sensor.temperatureCelsius, units).replace(/\.0(?=°)/, '')
    )
  return (
    <button
      type="button"
      data-testid={`marker-sensor-${sensor.id}`}
      data-status={status}
      aria-label={`${sensor.name}, ${locationName}`}
      aria-pressed={selected}
      onClick={editMode ? undefined : onClick}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      {...handlers}
      style={{
        left: `${position.x * 100}%`,
        top: `${position.y * 100}%`,
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.28)),
        touchAction: 'none',
      }}
      // A hovered or selected marker rises above its neighbours so its label is never painted over.
      className={`group absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full font-semibold tabular-nums shadow ring-4 select-none hover:z-30 ${STATUS_CLASS[status]} ${
        showLabel ? 'z-30' : selected ? 'z-20' : 'z-10'
      } ${
        selected ? 'outline outline-3 outline-primary outline-offset-2' : ''
      } ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
    >
      {reading}
      <span
        className={`pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded bg-base-100 px-2 py-0.5 text-xs text-base-content shadow transition-opacity ${
          showLabel ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        {sensor.name}
        <span className="text-base-content/60"> · {locationName}</span>
        {offline &&
          offlineTooltip(sensor, units)
            .split('\n')
            .map((line) => (
              <span key={line} className="block font-normal">
                {line}
              </span>
            ))}
      </span>
    </button>
  )
}
